import { MouseEvent } from 'react';
import {
  BsEnvelope,
  BsFileEarmarkArrowDownFill,
  BsFileEarmarkText,
  BsFiletypePdf,
  BsFillGeoAltFill,
  BsFillPhoneFill,
  BsGlobe,
  BsStar
} from 'react-icons/bs';
import { ReactComponent as LedgerIcon } from '../../../../../../../assets/icons/ledger.svg';
import { IconButton } from '../../../../../../../components/iconLink/IconButton';
import { IconLink } from '../../../../../../../components/iconLink/IconLink';
import { useToast } from '../../../../../../../context/ToastContext';
import { useDocumentDownload } from '../../../../../../../hooks/useDocumentDownload';
import { getCurrentUser } from '../../../../../../../services/helper';
import { Course } from '../../../../../../../types/course.type';
import { formatDate, getCurrentDateOnly } from '../../../../../../../utils/dateUtils';
import { SessionDocument } from '../SessionDetail.types';

const currentUser = getCurrentUser();

// Mirrors the API's checks in CourseService.sendReviewRequests
const canSendReviewRequests = (course: Course | undefined) =>
  !!currentUser?.isAdmin &&
  course?.payment_type === 'website' &&
  course.status !== 'cancelled' &&
  course.status !== 'offline' &&
  !!course.ends_on &&
  course.ends_on < getCurrentDateOnly() &&
  !!course.venue?.location?.consumer_fusion_business_id;

interface Props {
  course: Course | undefined;
  onPDFClick: (mode: string) => void;
  onSendTextReminder: (type: string) => void;
}

export const SessionResourcesSection = ({ course, onPDFClick, onSendTextReminder }: Props) => {
  const { downloadDocument } = useDocumentDownload();
  const { addToast } = useToast();

  const handleDocumentClick = async (
    event: MouseEvent<HTMLButtonElement>,
    doc: SessionDocument
  ) => {
    event.preventDefault();
    try {
      await downloadDocument(doc.id);
    } catch (error) {
      addToast(
        error instanceof Error ? error.message : 'Unable to open this document',
        'error'
      );
    }
  };

  return (
    <>
      <span
        className="fw-semibold text-secondary"
        style={{ fontSize: '0.875rem' }}
      >
        Links
      </span>
      {!currentUser?.isInstructor && (
        <IconLink
          icon={<BsGlobe size={24} />}
          text={course?.title ?? 'Website'}
          url={`/sessions/${course?.slug ?? course?.id}`}
        />
      )}
      {course?.venue && (
        <IconLink
          icon={<BsFillGeoAltFill size={24} />}
          text={course.venue.title ?? 'Venue'}
          url={`/admin/places/venues/${course.venue.id}`}
        />
      )}
      {!currentUser?.isInstructor && (
        <IconLink
          icon={<LedgerIcon />}
          text="Session Financials"
          url={`/admin/schedule/sessions/${course?.id}/ledger`}
        />
      )}
      <IconLink
        icon={<BsFiletypePdf size={24} />}
        text="Sign-In Sheet"
        onclick={() => onPDFClick('sign-in-sheet')}
      />
      {course?.payment_type === 'website' && (
        <IconLink
          icon={<BsFiletypePdf size={24} />}
          text="Roster"
          onclick={() => onPDFClick('roster')}
        />
      )}
      {course?.payment_type === 'onsite' && (
        <IconLink
          icon={<BsFileEarmarkText size={24} />}
          text="Waiver Form"
          url={`/waivers/new?course_id=${course?.id}`}
          target="_blank"
        />
      )}
      {!currentUser?.isInstructor && (
        <IconButton
          icon={<BsEnvelope size={24} />}
          text="Send Welcome Email"
          handleFunction={() => onSendTextReminder('welcome-course')}
        />
      )}
      {!currentUser?.isInstructor && (
        <IconButton
          icon={<BsEnvelope size={24} />}
          text="Send Thank You Email"
          handleFunction={() => onSendTextReminder('thank-course')}
        />
      )}
      {canSendReviewRequests(course) &&
        (course?.review_requested_at ? (
          <span className="d-flex align-items-center gap-2 text-secondary">
            <BsStar size={24} />
            Review requests sent {formatDate(new Date(course.review_requested_at))}
          </span>
        ) : (
          <IconButton
            icon={<BsStar size={24} />}
            text="Send Review Requests"
            handleFunction={() => onSendTextReminder('review-request')}
          />
        ))}

      {currentUser?.isAdmin && course?.venue && course.venue.contacts.length > 0 && (
        <IconButton
          icon={<BsEnvelope size={24} />}
          text="Send Roster/Waiver"
          handleFunction={() => onSendTextReminder('remind')}
        />
      )}
      {currentUser?.isSuper && (
        <IconButton
          icon={<BsFillPhoneFill size={24} />}
          text="Test SMS Reminder"
          handleFunction={() => onSendTextReminder('send-text-reminder')}
        />
      )}
      <span
        className="fw-semibold text-secondary mt-2"
        style={{ fontSize: '0.875rem' }}
      >
        Documents
      </span>
      {(course?.documents as SessionDocument[] | undefined)?.length ? (
        (course?.documents as SessionDocument[]).map((doc) => (
          <button
            key={doc.id}
            type="button"
            onClick={(e) => handleDocumentClick(e, doc)}
            style={{
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              textDecoration: 'none',
              background: 'none',
              border: 'none',
              padding: 0,
              textAlign: 'left'
            }}
            className="text-info fw-bolder"
          >
            <BsFileEarmarkArrowDownFill size={24} />
            {doc.title}
          </button>
        ))
      ) : (
        <span className="fst-italic">No documents found</span>
      )}
    </>
  );
};
